/**
 * THE Projectly document contract — single definition of the shapes both
 * clients read and write, plus the parsers that apply render-side fallbacks.
 *
 * ⚠ This file is VENDORED IDENTICALLY in two repos:
 *      projectly-io/src/shared/projectlyContract.ts   (canonical)
 *      projectly-app/src/shared/projectlyContract.ts  (copy)
 *   Edit it HERE, then run  scripts/sync-contract.sh  to push the copy to the
 *   app repo. Each repo's checks fail if its copy drifts from CONTRACT_HASH.
 *
 * Pure TypeScript on purpose: no Firebase imports, no platform imports, so
 * CRA, Metro and Node (functions tests) can all consume it. Parsers take the
 * plain object from `snapshot.data()`.
 *
 * Firestore paths:
 *   workspaces/{workspaceId}
 *     projects/{projectId}            ProjectDoc
 *       tasks/{taskId}                TaskDoc
 *       milestones/{milestoneId}      MilestoneDoc
 *
 * Dates (`start`, `due`, `date`) are ISO `YYYY-MM-DD` strings — never
 * Timestamps. The LAST entry of `statuses` counts as "done".
 */

// ── Custom task fields ───────────────────────────────────────────────────────
// Defined per project (e.g. a "Supplier" field), filled per task in
// `TaskDoc.values` keyed by the field's id. Field ids are stable; names are
// display-only and freely renamable.

export type TaskFieldType = 'text' | 'select' | 'number' | 'date';

export interface TaskFieldDef {
  id: string;
  name: string;
  type: TaskFieldType;
  /** For `select` fields. */
  options?: string[];
  required?: boolean;
}

// ── Document shapes (what is WRITTEN) ────────────────────────────────────────

export interface CommentDoc {
  id: string;
  authorId?: string;
  automation?: boolean;
  /** ISO instant for app-written comments; legacy data holds human strings. */
  ts: string;
  text: string;
  /** Uids tagged in the comment (each gets a one-off notification). */
  mentions?: string[];
}

/** Comments present in `after` but not in `before` (matched by id). Pure. */
export function newComments(before: any, after: any): CommentDoc[] {
  const prevIds = new Set(((before?.comments ?? []) as any[]).map(c => c?.id));
  return ((after?.comments ?? []) as any[]).filter(c => c?.id && !prevIds.has(c.id));
}

export type TaskPriority = 'High' | 'Medium' | 'Low';

export interface TaskDoc {
  title: string;
  status: string;
  assigneeId: string;
  start: string;
  due: string;
  priority: TaskPriority;
  comments: CommentDoc[];
  /** Custom field values, keyed by TaskFieldDef.id. */
  values?: Record<string, string>;
  /** Uids subscribed to update notifications for this task. */
  watchers?: string[];
}

export interface ProjectDoc {
  name: string;
  icon: string;
  description: string;
  members: string[];
  statuses: string[];
  statusColors: Record<string, string>;
  taskFields?: TaskFieldDef[];
}

export interface MilestoneDoc {
  name: string;
  date: string;
  done: boolean;
}

// ── Parsed shapes (what screens RENDER) ──────────────────────────────────────

export interface Task extends TaskDoc {
  id: string;
  values: Record<string, string>;
  watchers: string[];
}

export interface Milestone extends MilestoneDoc { id: string }

export interface Project {
  id: string;
  name: string;
  icon: string;
  description: string;
  memberIds: string[];
  statuses: string[];
  statusColors: Record<string, string>;
  taskFields: TaskFieldDef[];
  milestones: Milestone[];
  tasks: Task[];
}

// ── Defaults (shared so both clients degrade identically) ────────────────────

export const DEFAULT_STATUSES = ['Backlog', 'In progress', 'Review', 'Done'];
export const DEFAULT_STATUS_COLORS: Record<string, string> = {
  Backlog: '#94a3b8', 'In progress': '#f59e0b', Review: '#8b5cf6', Done: '#10b981',
};
export const DEFAULT_ICON = '📁';
export const PRIORITIES: TaskPriority[] = ['High', 'Medium', 'Low'];

// ── Parsers (render-side fallbacks live HERE and nowhere else) ───────────────
// If a fallback fires on a freshly written document, the write path has
// drifted from this contract — that is the bug class this file exists to kill.

export function parseTaskField(f: any): TaskFieldDef | null {
  if (!f || typeof f.id !== 'string' || !f.id) return null;
  return {
    id: f.id,
    name: typeof f.name === 'string' && f.name ? f.name : 'Field',
    type: (['text', 'select', 'number', 'date'] as const).includes(f.type) ? f.type : 'text',
    ...(Array.isArray(f.options) ? { options: f.options.map(String) } : {}),
    ...(f.required ? { required: true } : {}),
  };
}

export function parseProject(id: string, d: any): Project {
  return {
    id,
    name: d?.name ?? 'Untitled project',
    icon: d?.icon ?? DEFAULT_ICON,
    description: d?.description ?? '',
    memberIds: Array.isArray(d?.members) ? d.members : [],
    statuses: Array.isArray(d?.statuses) && d.statuses.length ? d.statuses : DEFAULT_STATUSES,
    statusColors: d?.statusColors ?? DEFAULT_STATUS_COLORS,
    taskFields: Array.isArray(d?.taskFields)
      ? d.taskFields.map(parseTaskField).filter((f: TaskFieldDef | null): f is TaskFieldDef => !!f)
      : [],
    milestones: Array.isArray(d?.milestones) ? d.milestones.map((m: any) => parseMilestone(m?.id ?? '', m)) : [],
    tasks: [],
  };
}

export function parseTask(id: string, d: any): Task {
  return {
    id,
    title: d?.title ?? '(untitled)',
    status: d?.status ?? '',
    assigneeId: d?.assigneeId ?? '',
    start: d?.start ?? '',
    due: d?.due ?? '',
    priority: PRIORITIES.includes(d?.priority) ? d.priority : 'Medium',
    comments: Array.isArray(d?.comments) ? d.comments : [],
    values: d?.values && typeof d.values === 'object' ? d.values : {},
    watchers: Array.isArray(d?.watchers) ? d.watchers : [],
  };
}

export function parseMilestone(id: string, d: any): Milestone {
  return { id, name: d?.name ?? '', date: d?.date ?? '', done: !!d?.done };
}

/** The complete TaskDoc a client should write when creating a task. */
export function newTaskDoc(input: Partial<TaskDoc> & { title: string }): TaskDoc {
  return {
    title: input.title,
    status: input.status ?? '',
    assigneeId: input.assigneeId ?? '',
    start: input.start ?? new Date().toISOString().slice(0, 10),
    due: input.due ?? '',
    priority: input.priority ?? 'Medium',
    comments: input.comments ?? [],
    values: input.values ?? {},
    watchers: input.watchers ?? (input.assigneeId ? [input.assigneeId] : []),
  };
}

// ── Linked task collections ──────────────────────────────────────────────────
// A pipeline collection node may be LINKED to a project's tasks
// (node.data.linkedProjectId). The tasks then surface as records — same rows
// in the Data view, same conditions/triggers/actions in the engine — with the
// task document as the single source of truth (no mirror, no sync).
// Built-in task properties map to stable pseudo-field ids; custom task fields
// keep their own ids, so `values` merge cleanly.

export const TASK_RECORD_FIELD_MAP: Record<string, keyof TaskDoc> = {
  t_title: 'title',
  t_status: 'status',
  t_assignee: 'assigneeId',
  t_start: 'start',
  t_due: 'due',
  t_priority: 'priority',
};

/** Collection field definitions for a linked node, materialised from the
 *  project (statuses feed the Status options; custom fields append). */
export function taskRecordFieldDefs(project?: {
  statuses?: string[]; taskFields?: TaskFieldDef[];
} | null): TaskFieldDef[] {
  return [
    { id: 't_title', name: 'Title', type: 'text' },
    { id: 't_status', name: 'Status', type: 'select', options: project?.statuses ?? DEFAULT_STATUSES },
    { id: 't_assignee', name: 'Assignee', type: 'text' },
    { id: 't_start', name: 'Start', type: 'date' },
    { id: 't_due', name: 'Due', type: 'date' },
    { id: 't_priority', name: 'Priority', type: 'select', options: [...PRIORITIES] },
    ...(project?.taskFields ?? []),
  ];
}

/** A task document viewed as a record's `values`. */
export function taskToRecordValues(task: any): Record<string, string> {
  return {
    t_title: task?.title ?? '',
    t_status: task?.status ?? '',
    t_assignee: task?.assigneeId ?? '',
    t_start: task?.start ?? '',
    t_due: task?.due ?? '',
    t_priority: task?.priority ?? '',
    ...(task?.values && typeof task.values === 'object' ? task.values : {}),
  };
}

/** Record-shaped partial values → the Firestore update on the task doc.
 *  Unknown field ids are treated as custom fields (values.{id}). */
export function recordPatchToTaskUpdate(patch: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(patch)) {
    const mapped = TASK_RECORD_FIELD_MAP[k];
    if (mapped) out[mapped] = v;
    else out[`values.${k}`] = v;
  }
  return out;
}

/** Record values from a create_record/add-row → a complete new TaskDoc. */
export function recordValuesToNewTaskDoc(
  values: Record<string, any>, statuses: string[] = DEFAULT_STATUSES,
): TaskDoc {
  const custom: Record<string, string> = {};
  for (const [k, v] of Object.entries(values || {})) {
    if (!TASK_RECORD_FIELD_MAP[k]) custom[k] = String(v ?? '');
  }
  return newTaskDoc({
    title: String(values?.t_title ?? '') || 'New task',
    status: String(values?.t_status ?? '') || statuses[0],
    assigneeId: String(values?.t_assignee ?? ''),
    start: values?.t_start != null && values.t_start !== '' ? String(values.t_start) : undefined,
    due: String(values?.t_due ?? ''),
    priority: PRIORITIES.includes(values?.t_priority) ? values.t_priority : 'Medium',
    values: custom,
  });
}

/**
 * Human summary of what changed on a task, for watcher notifications.
 * Returns null when nothing notification-worthy changed. Pure.
 */
export function describeTaskChange(
  before: any, after: any, fieldDefs: TaskFieldDef[] = [],
): string | null {
  const parts: string[] = [];
  if ((before?.status ?? '') !== (after?.status ?? '')) {
    parts.push(`status → ${after?.status || '—'}`);
  }
  if ((before?.assigneeId ?? '') !== (after?.assigneeId ?? '')) {
    parts.push(after?.assigneeId ? 'reassigned' : 'unassigned');
  }
  if ((before?.title ?? '') !== (after?.title ?? '')) {
    parts.push(`renamed to “${after?.title}”`);
  }
  if ((before?.due ?? '') !== (after?.due ?? '')) {
    parts.push(after?.due ? `due ${after.due}` : 'due date cleared');
  }
  if ((before?.priority ?? '') !== (after?.priority ?? '')) {
    parts.push(`priority ${after?.priority}`);
  }
  const bv = before?.values || {}, av = after?.values || {};
  for (const f of fieldDefs) {
    if ((bv[f.id] ?? '') !== (av[f.id] ?? '')) {
      parts.push(`${f.name}: ${av[f.id] || '—'}`);
    }
  }
  return parts.length ? parts.join(' · ') : null;
}

/** `{ done, total, pct, doneStatus }` — the LAST status counts as done. */
export function projectProgress(p: { statuses: string[]; tasks: Task[] }) {
  const doneStatus = p.statuses[p.statuses.length - 1];
  const total = p.tasks.length;
  const done = p.tasks.filter(t => t.status === doneStatus).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return { done, total, pct, doneStatus };
}

/** 'overdue' | 'soon' | null. Done tasks and dateless tasks return null. */
export function dueState(
  task: { due: string; status: string }, doneStatus: string,
  now: Date = new Date(), soonDays = 3,
): 'overdue' | 'soon' | null {
  if (!task.due || task.status === doneStatus) return null;
  const todayIso = now.toISOString().slice(0, 10);
  const today = new Date(todayIso + 'T00:00:00').getTime();
  const due = new Date(task.due + 'T00:00:00').getTime();
  if (isNaN(due)) return null;
  if (due < today) return 'overdue';
  if (due <= today + soonDays * 86400000) return 'soon';
  return null;
}
